/**
 * dd-policy.js
 * Progressive enhancement for the store policy pages (.dd-policy markup in
 * docs/store-policies/*.html).
 *
 * Why the accordion is built here and not in the markup: Shopify's storefront
 * sanitizes policy bodies on render and strips <details>, <summary>, <button>,
 * every data-* attribute, aria-expanded, and ids on non-heading elements. The
 * Admin body therefore ships each section as
 *
 *   <div class="dd-policy__item [is--open]">
 *     <h3 class="dd-policy__q" id="windows">Title</h3>
 *     <div class="dd-policy__a">...</div>
 *   </div>
 *
 * and this file turns it into an accessible disclosure: the h3 gets a
 * <button aria-expanded aria-controls>, the body gets an id and `hidden`.
 * Without JS the page is simply fully expanded, which is the correct fallback.
 *
 * Also: deep links open their section, Expand all / Collapse all, contents
 * scroll-spy (`.dd-policy__toc-link.is--current`), and print expands
 * everything then restores.
 *
 * Loaded by layout/theme.liquid only when request.page_type == 'policy'.
 * `window.ddPolicyInit` is exposed and idempotent so the screenshot harness
 * (.cursor/scripts/shot-policies.mjs --inject) can re-run it after swapping
 * the body markup.
 */
(function () {
  'use strict';

  function init() {
    var root = document.querySelector('.dd-policy');
    if (!root) return;
    if (window.__ddPolicyCleanup) window.__ddPolicyCleanup();

    var cleanups = [];
    var on = function (target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      cleanups.push(function () { target.removeEventListener(type, fn, opts); });
    };

    /* 0. Build the disclosures ------------------------------------- */
    var items = [];
    Array.prototype.forEach.call(root.querySelectorAll('.dd-policy__item'), function (item) {
      var heading = item.querySelector(':scope > .dd-policy__q');
      var panel = item.querySelector(':scope > .dd-policy__a');
      if (!heading || !panel) return;

      var btn = heading.querySelector('.dd-policy__q-btn');
      if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dd-policy__q-btn';
        var text = document.createElement('span');
        text.className = 'dd-policy__q-text';
        while (heading.firstChild) {
          var node = heading.firstChild;
          if (node.nodeType === 1 && node.classList.contains('dd-policy__q-num')) btn.appendChild(node);
          else text.appendChild(node);
        }
        btn.appendChild(text);
        var icon = document.createElement('span');
        icon.className = 'dd-policy__q-icon';
        icon.setAttribute('aria-hidden', 'true');
        btn.appendChild(icon);
        heading.appendChild(btn);
      }
      if (heading.id && !panel.id) panel.id = heading.id + '-panel';
      if (panel.id) btn.setAttribute('aria-controls', panel.id);

      var entry = { item: item, heading: heading, btn: btn, panel: panel };
      entry.set = function (open) {
        item.classList.toggle('is--open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.hidden = !open;
      };
      entry.isOpen = function () { return !panel.hidden; };
      entry.set(item.classList.contains('is--open'));
      on(btn, 'click', function () {
        entry.set(!entry.isOpen());
        syncExpandLabel();
      });
      items.push(entry);
    });
    root.classList.add('is--enhanced');

    var toc = root.querySelector('.dd-policy__toc');
    if (toc) {
      toc.setAttribute('role', 'navigation');
      toc.setAttribute('aria-label', 'On this page');
    }
    var tocLinks = Array.prototype.slice.call(root.querySelectorAll('.dd-policy__toc-link'));

    /* 1. Deep links ------------------------------------------------ */
    function entryFor(el) {
      var item = el && el.closest ? el.closest('.dd-policy__item') : null;
      if (!item) return null;
      for (var i = 0; i < items.length; i++) if (items[i].item === item) return items[i];
      return null;
    }
    function openForHash(hash) {
      if (!hash || hash.length < 2) return;
      var target;
      try { target = root.querySelector(hash); } catch (e) { return; }
      if (!target) return;
      var entry = entryFor(target);
      if (entry && !entry.isOpen()) entry.set(true);
      syncExpandLabel();
    }
    openForHash(window.location.hash);
    on(window, 'hashchange', function () { openForHash(window.location.hash); });
    // Clicking the contents link for the already-current hash does not fire
    // hashchange, so open on click too.
    tocLinks.forEach(function (link) {
      on(link, 'click', function () { openForHash(link.getAttribute('href')); });
    });

    /* 2. Expand / collapse all ------------------------------------- */
    var toolbar = root.querySelector('.dd-policy__toolbar');
    var expandBtn = null;
    if (toolbar && items.length) {
      expandBtn = toolbar.querySelector('.dd-policy__expand');
      if (!expandBtn) {
        expandBtn = document.createElement('button');
        expandBtn.type = 'button';
        expandBtn.className = 'dd-policy__expand';
        toolbar.appendChild(expandBtn);
      }
    }
    function allOpen() {
      return items.length > 0 && items.every(function (e) { return e.isOpen(); });
    }
    function syncExpandLabel() {
      if (!expandBtn) return;
      var open = allOpen();
      expandBtn.textContent = open ? 'Collapse all' : 'Expand all';
      expandBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    if (expandBtn) {
      on(expandBtn, 'click', function () {
        var open = !allOpen();
        items.forEach(function (e) { e.set(open); });
        syncExpandLabel();
      });
      syncExpandLabel();
    }

    /* 3. Contents scroll-spy --------------------------------------- */
    var byId = {};
    tocLinks.forEach(function (link) {
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) === '#') byId[href.slice(1)] = link;
    });
    // Ids sit on headings; spy on the block that heading belongs to so the
    // whole section (not just its title row) counts as "current".
    var spied = Object.keys(byId).map(function (id) {
      var el = document.getElementById(id);
      if (!el) return null;
      var block = el.closest('.dd-policy__item, .dd-policy__card, .dd-policy__help') || el;
      block.__ddSpyId = id;
      return block;
    }).filter(Boolean);
    function setCurrent(id) {
      tocLinks.forEach(function (link) {
        var isCurrent = link === byId[id];
        link.classList.toggle('is--current', isCurrent);
        if (isCurrent) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }
    if (spied.length && 'IntersectionObserver' in window) {
      var offset = parseInt(getComputedStyle(root).getPropertyValue('--policy-offset'), 10) || 120;
      var visible = new Map();
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { visible.set(e.target, e.isIntersecting); });
        var first = spied.find(function (el) { return visible.get(el); });
        if (first) setCurrent(first.__ddSpyId);
      }, { rootMargin: '-' + offset + 'px 0px -55% 0px', threshold: 0 });
      spied.forEach(function (el) { io.observe(el); });
      cleanups.push(function () { io.disconnect(); });
    }

    /* 4. Print ------------------------------------------------------ */
    var printRestore = null;
    on(window, 'beforeprint', function () {
      printRestore = items.map(function (e) { return e.isOpen(); });
      items.forEach(function (e) { e.set(true); });
    });
    on(window, 'afterprint', function () {
      if (!printRestore) return;
      items.forEach(function (e, i) { e.set(printRestore[i]); });
      printRestore = null;
      syncExpandLabel();
    });

    window.__ddPolicyCleanup = function () {
      cleanups.forEach(function (fn) { fn(); });
      cleanups = [];
    };
  }

  window.ddPolicyInit = init;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
